import { Match, Show, Switch, type VoidComponent } from "solid-js";
import type { User } from "../../state/user";
import type { Invite } from "../../state/invite";
import type { Group } from "../../state/group";
import { useTranslation } from "../../state/i18n";
import Text from "../../components/text/Text";
import Pill from "../../components/pill/Pill";
import { narrow } from "../../utils/solidUtils";
import { isInvite, resolveUserOrInviteName } from "../../utils/userUtil";
import { resolveName as resolveGroupName } from "../../utils/groupUtil";
import { GroupAvatar, MemberAvatar } from "../../components/avatar/Avatar.tsx";

export type MemberIdentity = { kind: "user"; userOrInvite: User | Invite } | { kind: "group"; group: Group };

export const MemberIdentityCell: VoidComponent<{ member: MemberIdentity }> = (props) => {
  const { t } = useTranslation();
  return (
    <Switch>
      <Match when={narrow(props.member, (m) => m.kind === "user")}>
        {(userMember) => {
          const userOrInvite = userMember().userOrInvite;
          return (
            <div role="cell" class="flex min-w-0 items-center gap-2.5 px-3 py-4">
              <MemberAvatar member={userOrInvite} size="md" />
              <div class="flex min-w-0 flex-col">
                <div class="flex items-center gap-2">
                  <Text
                    text={resolveUserOrInviteName(isInvite(userOrInvite), userOrInvite)}
                    as="span"
                    class="shrink text-base-medium text-primary"
                    truncate
                  />
                  <Show when={isInvite(userOrInvite)}>
                    <Pill variant="neutral" label={t(($) => $.USERS_PAGE_SSO_INVITED)} />
                  </Show>
                  <Show when={!isInvite(userOrInvite) && userOrInvite.externallyManaged}>
                    <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                  </Show>
                </div>
                <Show when={!isInvite(userOrInvite)}>
                  <Text text={userOrInvite.emailAddress} as="span" class="text-base-regular text-secondary" truncate />
                </Show>
              </div>
            </div>
          );
        }}
      </Match>
      <Match when={narrow(props.member, (m) => m.kind === "group")}>
        {(groupMember) => {
          const group = groupMember().group;
          return (
            <div role="cell" class="flex min-w-0 items-center gap-2.5 px-3 py-4">
              <GroupAvatar group={group} size="md" />
              <div class="flex min-w-0 items-center gap-2">
                <Text text={resolveGroupName(group)} as="span" class="text-base-medium text-primary" truncate />
                <Show when={group.externallyManaged}>
                  <Pill variant="informative" label={t(($) => $.USERS_PAGE_SSO_MANAGED)} />
                </Show>
              </div>
            </div>
          );
        }}
      </Match>
    </Switch>
  );
};
